import React, { useRef, useState } from "react";

export default function PhotoUploader({ value = [], onChange }) {
  const inputRef = useRef(null);
  const [previews, setPreviews] = useState(value);
  const [isDragOver, setIsDragOver] = useState(false);

  function handleFiles(files) {
    const arr = Array.from(files).slice(0, 6);
    const readers = arr.map(
      (file) =>
        new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res({ name: file.name, data: r.result });
          r.readAsDataURL(file);
        })
    );
    Promise.all(readers).then((imgs) => {
      const next = [...previews, ...imgs.map((i) => i.data)].slice(0, 6);
      setPreviews(next);
      onChange?.(next);
    });
  }

  function handleRemove(index) {
    const next = previews.filter((_, i) => i !== index);
    setPreviews(next);
    onChange?.(next);
  }

  function triggerFile() {
    inputRef.current?.click();
  }

  function handleDragOver(e) {
    e.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setIsDragOver(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  }

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
          isDragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-slate-300 hover:border-slate-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700">
              {previews.length === 0 ? 'Upload photos' : 'Add more photos'}
            </p>
            <p className="text-xs text-slate-500">
              Drag & drop or click to browse
            </p>
          </div>
          <button
            type="button"
            onClick={triggerFile}
            className="btn-secondary text-sm"
          >
            Choose Files
          </button>
        </div>
      </div>

      {/* Photo Grid */}
      {previews.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {previews.map((preview, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square rounded-xl overflow-hidden bg-slate-100">
                <img 
                  src={preview} 
                  alt={`Upload ${index + 1}`} 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Remove Button */}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                aria-label="Remove photo"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Photo Number Badge */}
              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                {index + 1}
              </div>
            </div>
          ))}
          
          {/* Add More Button */}
          {previews.length < 6 && (
            <button
              type="button"
              onClick={triggerFile}
              className="aspect-square border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-500 hover:border-slate-400 hover:text-slate-600 transition-colors duration-200"
            >
              <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="text-sm font-medium">Add Photo</span>
            </button>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="flex items-start gap-2 text-xs text-slate-500">
        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p>Upload up to 6 high-quality photos. Include different angles and any wear or damage.</p>
          <p className="mt-1">Recommended: Well-lit photos with a clean background.</p>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}